
import { supabase } from '@/integrations/supabase/client';

export interface FileUploadResult {
  id: string;
  file_name: string;
  file_type: string;
  file_size: number;
  storage_path?: string;
  connection_id: string;
  organisation_id?: string;
}

export const getFileType = (fileName: string): string => {
  const extension = fileName.split('.').pop()?.toLowerCase() || '';
  
  const typeMap: { [key: string]: string } = {
    'csv': 'CSV',
    'xlsx': 'Excel',
    'xls': 'Excel', 
    'pdf': 'PDF',
    'doc': 'Word',
    'docx': 'Word',
    'txt': 'Text',
    'json': 'JSON',
    'xml': 'XML',
    'jpg': 'Image',
    'jpeg': 'Image',
    'png': 'Image',
    'gif': 'Image',
    'svg': 'Image',
    'mp4': 'Video',
    'avi': 'Video',
    'mov': 'Video',
    'zip': 'Archive',
    'rar': 'Archive',
    '7z': 'Archive'
  };
  
  return typeMap[extension] || 'Other';
};

export const isCSVFile = (fileName: string): boolean => {
  return fileName.toLowerCase().endsWith('.csv');
};

export const uploadFileToStorage = async (
  file: File, 
  connectionId: string
): Promise<{ storage_path: string; error?: string }> => {
  try {
    const timestamp = Date.now();
    const sanitizedFileName = file.name.replace(/[^a-zA-Z0-9.-]/g, '_');
    const filePath = `${connectionId}/${timestamp}_${sanitizedFileName}`;
    
    const { data, error } = await supabase.storage
      .from('company-files')
      .upload(filePath, file, {
        cacheControl: '3600',
        upsert: false
      });

    if (error) {
      console.error('Storage upload error:', error);
      return { storage_path: '', error: error.message };
    }

    return { storage_path: data.path };
  } catch (error) {
    console.error('Upload error:', error);
    return { 
      storage_path: '', 
      error: error instanceof Error ? error.message : 'Upload failed' 
    };
  }
};

export const saveFileMetadata = async (
  file: File,
  connectionId: string,
  storagePath?: string,
  organisationId?: string
): Promise<{ data?: FileUploadResult; error?: string }> => {
  try {
    const fileRecord: Record<string, unknown> = {
      connection_id: connectionId,
      file_name: file.name,
      file_type: getFileType(file.name),
      file_size: file.size,
      storage_path: storagePath || null,
      content: null,
      system_table_type: isCSVFile(file.name) ? 'csv' : 'file'
    };

    if (organisationId) {
      fileRecord.organisation_id = organisationId;
    }

    const { data, error } = await supabase
      .from('file_imports')
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      .insert(fileRecord as any)
      .select()
      .single();

    if (error) {
      console.error('Database insert error:', error);
      return { error: error.message };
    }

    return { data: data as FileUploadResult };
  } catch (error) {
    console.error('Save metadata error:', error);
    return { 
      error: error instanceof Error ? error.message : 'Failed to save file metadata' 
    };
  }
};

export const processFileUpload = async (
  file: File,
  connectionId: string,
  organisationId?: string
): Promise<{ data?: FileUploadResult; error?: string }> => {
  try {
    if (isCSVFile(file.name)) {
      // For CSV files, we still use the existing CSV processing
      // This will be handled by the existing createDatabaseForCsv function
      return await saveFileMetadata(file, connectionId, undefined, organisationId);
    } else {
      // For non-CSV files, upload to storage first
      const uploadResult = await uploadFileToStorage(file, connectionId);
      
      if (uploadResult.error) {
        return { error: uploadResult.error };
      }
      
      // Then save metadata with storage path
      return await saveFileMetadata(file, connectionId, uploadResult.storage_path, organisationId);
    }
  } catch (error) {
    console.error('Process file upload error:', error);
    return { 
      error: error instanceof Error ? error.message : 'Failed to process file upload' 
    };
  }
};

export const getFileDownloadUrl = async (storagePath: string): Promise<string | null> => {
  try {
    const { data } = await supabase.storage
      .from('company-files')
      .getPublicUrl(storagePath);
    
    return data.publicUrl;
  } catch (error) {
    console.error('Error getting download URL:', error);
    return null;
  }
};

export const deleteFile = async (
  fileId: string, 
  storagePath?: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    // Delete from storage if path exists
    if (storagePath) {
      const { error: storageError } = await supabase.storage
        .from('company-files')
        .remove([storagePath]);
      
      if (storageError) {
        console.error('Storage deletion error:', storageError);
      }
    }
    
    // Delete metadata from database
    const { error: dbError } = await supabase
      .from('file_imports')
      .delete()
      .eq('id', fileId);
    
    if (dbError) {
      console.error('Database deletion error:', dbError);
      return { success: false, error: dbError.message };
    }
    
    return { success: true };
  } catch (error) {
    console.error('Delete file error:', error);
    return { 
      success: false, 
      error: error instanceof Error ? error.message : 'Failed to delete file' 
    };
  }
};
